import type { QueryClient } from '@tanstack/react-query';
import { queryOptions } from '@tanstack/react-query';
import { getLocationPreferences } from '@/features/locations/presentation/actions/get-location-preferences';
import { getEpagosTerminals } from '@/features/totems/presentation/actions/get-epagos-terminals';
import { getStaticQr } from '@/features/totems/presentation/actions/get-static-qr';
import { getMenuSnapshot } from '@/features/totems/presentation/actions/web-totem/get-menu-snapshot';
import { getTotemPaymentMethods } from '@/features/totems/presentation/actions/web-totem/get-totem-payment-methods';
import { getTotemWithLocation } from '@/features/totems/presentation/actions/web-totem/get-totem-with-location';

/**
 * Jerarquía de `totems`:
 * - `list(slug)`: listado del local (por slug del local, es lo que recibe la query).
 * - `detail(totemId)`: un tótem; `context(totemId)` cuelga de él, así que invalidar `detail` también invalida el contexto.
 * Las demás claves tienen raíz propia para que invalidar `all` no refresque datos ajenos (menú, preferencias, etc.).
 */
export const totemsKeys = {
  all: ['totems'] as const,
  lists: () => [...totemsKeys.all, 'list'] as const,
  list: (locationSlug: string) => [...totemsKeys.lists(), locationSlug] as const,
  details: () => [...totemsKeys.all, 'detail'] as const,
  detail: (totemId: string) => [...totemsKeys.details(), totemId] as const,
  context: (totemId: string) => [...totemsKeys.detail(totemId), 'context'] as const,
  menuSnapshot: (totemId: string, menuId: string | null | undefined) => ['menu-snapshot', totemId, menuId] as const,
  locationPreferences: (locationId: string | undefined) => ['location-preferences', locationId] as const,
  paymentMethods: (totemId: string | undefined, locationSlug?: string) =>
    ['totem-payment-methods', totemId, locationSlug] as const,
  epagosTerminals: (locationUuid: string | undefined) => ['epagos-terminals', locationUuid] as const,
  posdataGetnet: (integrationId: string | undefined, url: string | undefined) =>
    ['posdata-getnet', integrationId, url] as const,
  staticQr: (totemId: string) => ['static-qr', totemId] as const,
};

/** Refresca listado y detalles de tótems (el detalle antes quedaba desactualizado al invalidar solo el listado). */
export const invalidateTotems = (queryClient: QueryClient, locationSlug: string) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: totemsKeys.list(locationSlug) }),
    queryClient.invalidateQueries({ queryKey: totemsKeys.details() }),
  ]);

export const refetchTotems = (queryClient: QueryClient, locationSlug: string) =>
  Promise.all([
    queryClient.refetchQueries({ queryKey: totemsKeys.list(locationSlug) }),
    queryClient.refetchQueries({ queryKey: totemsKeys.details() }),
  ]);

export const totemContextQueryOptions = (totemId: string) =>
  queryOptions({
    queryKey: totemsKeys.context(totemId),
    queryFn: () => getTotemWithLocation({ totemId }),
  });

export const totemMenuSnapshotQueryOptions = (totemId: string, menuId: string | null | undefined) =>
  queryOptions({
    queryKey: totemsKeys.menuSnapshot(totemId, menuId),
    queryFn: () => getMenuSnapshot({ totemId, menuId: menuId! }),
    enabled: Boolean(totemId && menuId),
  });

export const locationPreferencesQueryOptions = (locationId: string | undefined) =>
  queryOptions({
    queryKey: totemsKeys.locationPreferences(locationId),
    queryFn: () => getLocationPreferences(locationId!),
    enabled: !!locationId,
  });

export const totemPaymentMethodsQueryOptions = (totemId: string | undefined, locationSlug?: string) =>
  queryOptions({
    queryKey: totemsKeys.paymentMethods(totemId, locationSlug),
    queryFn: () => getTotemPaymentMethods(totemId!, locationSlug),
    enabled: Boolean(totemId),
  });

export const epagosTerminalsQueryOptions = (locationUuid: string | undefined, enabled: boolean) =>
  queryOptions({
    queryKey: totemsKeys.epagosTerminals(locationUuid),
    queryFn: async () => {
      const result = await getEpagosTerminals(locationUuid!);
      if (!result.success) throw new Error(result.error);
      return result.terminals;
    },
    enabled: !!locationUuid && enabled,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

export const staticQrQueryOptions = (totemId: string) =>
  queryOptions({
    queryKey: totemsKeys.staticQr(totemId),
    queryFn: () => getStaticQr(totemId),
  });
